import { Suspense, lazy, useState, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Onboarding, useOnboarding } from "@/components/Onboarding";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { OfflineSyncIndicator } from "@/components/OfflineSyncIndicator";
import { ConnectionRestoredToast } from "@/components/ConnectionRestoredToast";
import { PostAuthSync } from "@/components/PostAuthSync";
import { Toaster } from "@/components/ui/toaster";

// Lazy load pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const FaceIdLogin = lazy(() => import("./pages/FaceIdLogin"));
const Transactions = lazy(() => import("./pages/Transactions"));
const AddTransaction = lazy(() => import("./pages/AddTransaction"));
const Categories = lazy(() => import("./pages/Categories"));
const EditCategory = lazy(() => import("./pages/EditCategory"));
const CategorySelect = lazy(() => import("./pages/CategorySelect"));
const AccountingSettings = lazy(() => import("./pages/AccountingSettings"));
const Settings = lazy(() => import("./pages/Settings"));
const Tags = lazy(() => import("./pages/Tags"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Notifications = lazy(() => import("./pages/Notifications"));
const NotificationSettings = lazy(() => import("./pages/NotificationSettings"));
const Budgets = lazy(() => import("./pages/Budgets"));
const EditBudget = lazy(() => import("./pages/EditBudget"));
const FirstDaySettings = lazy(() => import("./pages/FirstDaySettings"));
const Account = lazy(() => import("./pages/Account"));
const Help = lazy(() => import("./pages/Help"));
const About = lazy(() => import("./pages/About"));
const SupportDeveloper = lazy(() => import("./pages/SupportDeveloper"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// App content with onboarding check
function AppContent() {
  const { isCompleted } = useOnboarding();
  const [showOnboarding, setShowOnboarding] = useState(!isCompleted);

  // Check onboarding status on mount
  useEffect(() => {
    setShowOnboarding(!isCompleted);
  }, [isCompleted]);

  if (showOnboarding) {
    return <Onboarding onComplete={() => setShowOnboarding(false)} />;
  }

  // Get location for AnimatePresence key
  const location = useLocation();

  return (
    <Suspense fallback={<PageLoader />}>
      <AnimatePresence mode="wait" initial={false}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/login" element={<FaceIdLogin />} />
          <Route path="/face-id" element={<FaceIdLogin />} />
          <Route path="/transactions" element={<Transactions />} />
          <Route path="/transactions/new" element={<AddTransaction />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/categories/edit/:id" element={<EditCategory />} />
          <Route path="/categories/new" element={<EditCategory />} />
          <Route path="/category-select" element={<CategorySelect />} />
          <Route path="/accounting-settings" element={<AccountingSettings />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/tags" element={<Tags />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/notification-settings" element={<NotificationSettings />} />
          <Route path="/budgets" element={<Budgets />} />
          <Route path="/budgets/new" element={<EditBudget />} />
          <Route path="/budgets/edit/:id" element={<EditBudget />} />
          <Route path="/first-day-settings" element={<FirstDaySettings />} />
          <Route path="/account" element={<Account />} />
          <Route path="/help" element={<Help />} />
          <Route path="/about" element={<About />} />
          <Route path="/support-developer" element={<SupportDeveloper />} />
          <Route path="/terms" element={<Terms />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AnimatePresence>
    </Suspense>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <ErrorBoundary>
        <HashRouter>
          <OfflineSyncIndicator />
          <ConnectionRestoredToast />
          <PostAuthSync />
          <AppContent />
          <Toaster />
        </HashRouter>
      </ErrorBoundary>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
